import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString, TextareaField } from "common";
import { FormikHelpers } from "formik";
import React from "react";
import { useMutation } from "react-query";
import { CatalogItem, CatalogItemObject, CatalogType, UnitType } from "types";
import { object, string } from "yup";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

interface CreateCatalogItemModalProps {
    isOpen: boolean;
    onCreate: (catalogItem: CatalogItem) => void;
    onClose: () => void;
}

interface CreateUnitPayload {
    name: string;
    caption: string;
    unitType: UnitType;
    seller: { id: ID };
    object: CatalogItemObject;
    type: CatalogType;
}

export const CreateCatalogItemModal = ({ isOpen, onCreate, onClose }: CreateCatalogItemModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent onCreate={onCreate} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ onCreate, onClose }: Omit<CreateCatalogItemModalProps, "isOpen">) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const initialValues: CreateUnitPayload = {
        name: "",
        caption: "",
        seller: { id: seller.id },
        type: CatalogType.QUANTITY,
        unitType: UnitType.DEMOGRAPHIC,
        object: CatalogItemObject.GLOBAL_CATALOG_ITEM,
    };

    const { mutateAsync: createCatalogItem, isLoading: isSaving } = useMutation(async (payload: CreateUnitPayload) => {
        return api.post<CatalogItem>("/catalogItems", payload);
    });

    const handleSubmit = async (values: CreateUnitPayload, { setFieldError }: FormikHelpers<CreateUnitPayload>) => {
        try {
            const { data: createdCatalogItem } = await createCatalogItem(values);
            flash.success("Successfully created demographic");
            invalidate("catalogItems");
            invalidate("units");
            invalidate("lineItemTemplates");
            onCreate(createdCatalogItem);
            onClose();
        } catch (error: any) {
            if (error?.response?.data?.field.name) {
                setFieldError("name", " - demographic with this name already exists");
            }
        }
    };

    const validationSchema = object({
        name: requiredString(),
        description: string(),
    });

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validateOnChange={false}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, dirty }) => (
                <>
                    <Modal.Header>Create Demographic</Modal.Header>

                    <Modal.Body>
                        <fieldset className="space-y-6" disabled={isSaving}>
                            <FormField name="name" label="Name">
                                <InputField isRequired placeholder="Name" />
                            </FormField>

                            <FormField name="caption" label="Caption">
                                <TextareaField rows={5} placeholder="Caption" />
                            </FormField>
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton disabled={!dirty} isLoading={isSaving} onClick={handleSubmit}>
                            Create
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
